import React, { createContext, useContext, useEffect, useState } from 'react';
import { 
  GoogleAuthProvider, 
  signInWithPopup, 
  signOut, 
  onAuthStateChanged, 
  User as FirebaseUser 
} from 'firebase/auth';
import { doc, getDoc, setDoc, collection } from 'firebase/firestore';
import { auth, db } from '../config/firebase';

// User type with additional fields
interface User extends FirebaseUser {
  isAdmin?: boolean;
}

// Auth context type
interface AuthContextType {
  currentUser: User | null;
  loading: boolean;
  signInWithGoogle: () => Promise<void>;
  logout: () => Promise<void>;
}

// Create context with default values
const AuthContext = createContext<AuthContextType>({
  currentUser: null,
  loading: true,
  signInWithGoogle: async () => {},
  logout: async () => {},
});

// Custom hook to use auth context
export const useAuth = () => useContext(AuthContext);

// Auth provider component
export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  // Create or update user in Firestore
  const createOrUpdateUser = async (user: FirebaseUser) => {
    try {
      // Check if user exists in Firestore
      const userRef = doc(db, 'users', user.uid);
      const userSnap = await getDoc(userRef);
      
      if (!userSnap.exists()) {
        // Create new user document
        const userData = {
          uid: user.uid,
          email: user.email,
          displayName: user.displayName,
          firstName: user.displayName?.split(' ')[0] || '',
          lastName: user.displayName?.split(' ').slice(1).join(' ') || '',
          profilePicture: user.photoURL,
          role: 'user', // Default role
          createdAt: new Date(),
          updatedAt: new Date()
        };
        
        await setDoc(userRef, userData);
        
        // For the first user, make them an admin automatically
        const usersCollection = collection(db, 'users');
        const usersSnapshot = await getDoc(doc(usersCollection, 'count'));
        
        if (!usersSnapshot.exists()) {
          // This is the first user, make them admin
          await setDoc(userRef, { ...userData, role: 'admin' });

          // Create a count document to track user count
          await setDoc(doc(db, 'users', 'count'), { count: 1 });
        }
        
        return { ...userData };
      } else {
        // User exists, return their data
        return userSnap.data();
      }
    } catch (error) {
      console.error('Error creating/updating user:', error);
    }
  };

  // Sign in with Google
  const signInWithGoogle = async (): Promise<void> => {
    try {
      const provider = new GoogleAuthProvider();
      const result = await signInWithPopup(auth, provider);
      
      // Create or update user in Firestore
      await createOrUpdateUser(result.user);
    } catch (error) {
      console.error('Error signing in with Google:', error);
    }
  };

  // Logout function
  const logout = async (): Promise<void> => {
    try {
      await signOut(auth);
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  // Set up auth state listener
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        try {
          // Create or update user in Firestore
          const userData = await createOrUpdateUser(user);
          
          // Add admin status to the user object
          const extendedUser = user as User;
          extendedUser.isAdmin = userData?.role === 'admin';
          setCurrentUser(extendedUser);
        } catch (error) {
          console.error('Error processing user:', error);
          setCurrentUser(user as User);
        }
      } else {
        setCurrentUser(null);
      }
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const value = {
    currentUser,
    loading,
    signInWithGoogle,
    logout,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};

export default AuthContext; 